import React, { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../src/lib/supabase';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import ConfirmationModal from '../../src/components/ConfirmationModal';
import { Loader2, MessageCircle, Send } from 'lucide-react';

// Envio do acesso ao Portal do Consumidor por WhatsApp (#6138). Quem manda é o cron
// `enviar-acesso-portal`, um a cada 5 min de segunda a sexta, só com o modo ligado. Aqui o time
// liga/desliga, vê quanto falta e manda um teste antes. Quem recebe é decidido no banco
// (portal_acesso_situacao) — a tela só mostra as contagens de `portal_acesso_resumo`.

/** Mesmo valor de LIMITE_DIARIO em supabase/functions/_shared/portalAcessoAviso.ts. */
const LIMITE_DIARIO = 100;

interface ResumoEnvio {
    modo: string;
    por_situacao: Record<string, number>;
    enviados_hoje: number;
    falhas_hoje: number;
    ultimo_erro: string | null;
    ultimo_envio: string | null;
}

/** Na ordem em que aparecem. As situações vêm de public.portal_acesso_situacao(). */
const SITUACOES: { chave: string; rotulo: string; dica: string }[] = [
    { chave: 'enviado', rotulo: 'Já receberam', dica: 'Não recebem de novo.' },
    { chave: 'pronto', rotulo: 'Na fila', dica: 'Clientes com WhatsApp só deles que ainda não entraram no portal.' },
    { chave: 'conferir', rotulo: 'Conferir no UaizyChat', dica: 'O UaizyChat não respondeu a tempo: a mensagem pode ter saído. Não são reenviados sozinhos.' },
    { chave: 'telefone_compartilhado', rotulo: 'Telefone de mais de uma pessoa', dica: 'O mesmo número aparece em UCs de outra pessoa (vendedor, parceira, família) ou é de empresa parceira. Ficam de fora.' },
    { chave: 'sem_whatsapp', rotulo: 'Sem WhatsApp', dica: 'Nenhuma UC de cliente com WhatsApp válido.' },
    { chave: 'nao_cliente', rotulo: 'Não são clientes ativos', dica: 'Só têm UC de negociação perdida, reprovada ou em andamento, cancelada ou de cliente próprio da parceira.' },
    { chave: 'ja_entrou', rotulo: 'Já entraram no portal', dica: 'Já conhecem o portal.' },
    { chave: 'falhou', rotulo: 'Falharam 3 vezes', dica: 'Saem da fila automática.' },
    { chave: 'interna', rotulo: 'Contas internas', dica: 'Contas de teste e de empresas do grupo Vigor ou parceiras. Nunca recebem.' },
];

const dataHoraBr = (iso: string): string => new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

const INPUT_CLASS = 'w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700/50 text-sm font-normal text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none';

const EnvioAcessoPortalCard: React.FC = () => {
    const { addToast } = useToast();
    const { profile } = useAuth();
    const [resumo, setResumo] = useState<ResumoEnvio | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [mudandoModo, setMudandoModo] = useState(false);
    const [confirmarLigar, setConfirmarLigar] = useState(false);

    const [testeAberto, setTesteAberto] = useState(false);
    const [testeDoc, setTesteDoc] = useState('');
    const [testeTelefone, setTesteTelefone] = useState('');
    const [enviandoTeste, setEnviandoTeste] = useState(false);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const { data, error } = await supabase.rpc('portal_acesso_resumo');
            if (error) throw error;
            setResumo(data as ResumoEnvio);
        } catch (err) {
            console.error('Erro ao carregar o resumo do envio do acesso:', err);
            addToast('Não foi possível carregar o envio do acesso por WhatsApp.', 'error');
        } finally {
            setCarregando(false);
        }
    }, [addToast]);

    useEffect(() => { carregar(); }, [carregar]);

    // O teste vai, por padrão, para o WhatsApp de quem está logado.
    useEffect(() => {
        if (testeAberto && !testeTelefone && profile?.whatsapp) setTesteTelefone(profile.whatsapp);
    }, [testeAberto, testeTelefone, profile?.whatsapp]);

    const ligado = resumo?.modo === 'ligado';

    const mudarModo = async (novo: 'ligado' | 'desligado') => {
        setMudandoModo(true);
        try {
            const { error } = await supabase.from('system_settings').update({ value: novo }).eq('key', 'portal_acesso_envio_modo');
            if (error) throw error;
            addToast(novo === 'ligado' ? 'Envio ligado. O próximo sai na próxima janela de 5 minutos.' : 'Envio desligado.', 'success');
            await carregar();
        } catch (err) {
            console.error('Erro ao mudar o modo do envio do acesso:', err);
            addToast('Não foi possível mudar o envio agora.', 'error');
        } finally {
            setMudandoModo(false);
            setConfirmarLigar(false);
        }
    };

    const enviarTeste = async () => {
        setEnviandoTeste(true);
        try {
            const { data, error } = await supabase.functions.invoke('enviar-acesso-portal', {
                body: { acao: 'teste', cpf_cnpj: testeDoc, telefone: testeTelefone },
            });
            if (error) {
                // O SDK engole o motivo em resposta não-2xx; ele vem em error.context.
                const detalhe = await (error as any)?.context?.json?.().catch(() => null);
                throw new Error(detalhe?.error || error.message);
            }
            if (!data?.success) throw new Error(data?.error || 'O envio não saiu.');
            addToast(`Teste enviado para ${data.telefone}. Confira no WhatsApp.`, 'success');
            setTesteAberto(false);
        } catch (err) {
            addToast(err instanceof Error ? err.message : 'O teste não saiu.', 'error');
        } finally {
            setEnviandoTeste(false);
        }
    };

    const n = (chave: string) => resumo?.por_situacao?.[chave] ?? 0;
    const progressoHoje = Math.min(100, Math.round(((resumo?.enviados_hoje ?? 0) / LIMITE_DIARIO) * 100));

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-6 space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <MessageCircle size={18} className="text-primary" />
                        Envio do acesso por WhatsApp
                        {resumo && (
                            <span className={`ml-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${ligado ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                                {ligado ? 'Ligado' : 'Desligado'}
                            </span>
                        )}
                    </h3>
                    <p className="text-xs text-gray-400 mt-1 max-w-xl">
                        Avisa pelo número oficial da Vigor que a conta existe e como entrar (CPF/CNPJ e os 6 primeiros números como
                        senha). Até {LIMITE_DIARIO} por dia, um a cada 5 minutos, de segunda a sexta, das 9h às 18h. Quem já recebeu
                        não recebe de novo.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setTesteAberto((v) => !v)}
                        className="flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-primary hover:text-primary transition-colors"
                    >
                        <Send size={14} /> Enviar teste
                    </button>
                    <button
                        onClick={() => (ligado ? mudarModo('desligado') : setConfirmarLigar(true))}
                        disabled={carregando || mudandoModo || !resumo}
                        className={`flex items-center gap-2 text-sm font-bold px-4 py-2 rounded-lg transition-colors disabled:opacity-50 ${ligado ? 'bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400' : 'bg-primary text-white hover:bg-primary/90'}`}
                    >
                        {mudandoModo && <Loader2 size={14} className="animate-spin" />}
                        {ligado ? 'Desligar envio' : 'Ligar envio'}
                    </button>
                </div>
            </div>

            {testeAberto && (
                <div className="rounded-xl bg-gray-50 dark:bg-gray-700/30 p-4 grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 space-y-1">
                        <span>CPF/CNPJ da conta usada no exemplo</span>
                        <input value={testeDoc} onChange={(e) => setTesteDoc(e.target.value)} placeholder="Use uma conta de teste" className={INPUT_CLASS} />
                    </label>
                    <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 space-y-1">
                        <span>WhatsApp que recebe</span>
                        <input value={testeTelefone} onChange={(e) => setTesteTelefone(e.target.value)} placeholder="(62) 90000-0000" className={INPUT_CLASS} />
                    </label>
                    <button
                        onClick={enviarTeste}
                        disabled={enviandoTeste || !testeDoc.trim() || !testeTelefone.trim()}
                        className="flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-sm px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                    >
                        {enviandoTeste && <Loader2 size={14} className="animate-spin" />}
                        Enviar
                    </button>
                    <p className="text-xs text-gray-400 sm:col-span-3">
                        A mensagem sai com o nome dessa conta. Fica registrada como teste e não tira ninguém da fila.
                    </p>
                </div>
            )}

            {carregando && !resumo ? (
                <div className="text-sm text-gray-400 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Carregando...</div>
            ) : resumo && (
                <>
                    <div className="space-y-1.5">
                        <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
                            <span>
                                Hoje: <strong className="text-gray-900 dark:text-white">{resumo.enviados_hoje}</strong> de {LIMITE_DIARIO}
                                {resumo.falhas_hoje > 0 && <span className="text-red-500 ml-3">{resumo.falhas_hoje} falha(s)</span>}
                                {resumo.falhas_hoje >= 3 && <span className="text-red-500"> — envio pausado até amanhã</span>}
                            </span>
                            <span className="text-xs text-gray-400">
                                {resumo.ultimo_envio && <>Último envio: {dataHoraBr(resumo.ultimo_envio)}</>}
                                {n('pronto') > 0 && <>{resumo.ultimo_envio && ' · '}fila acaba em ~{Math.ceil(n('pronto') / LIMITE_DIARIO)} dia(s) de envio</>}
                            </span>
                        </div>
                        <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                            <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${progressoHoje}%` }} />
                        </div>
                        {resumo.ultimo_erro && (
                            <p className="text-xs text-red-500 break-words">Último erro de hoje: {resumo.ultimo_erro}</p>
                        )}
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                        {SITUACOES.map((s) => (
                            <div key={s.chave} title={s.dica} className="rounded-xl border border-gray-100 dark:border-gray-700 px-3 py-2 cursor-help">
                                <p className="text-lg font-bold text-gray-900 dark:text-white">{n(s.chave).toLocaleString('pt-BR')}</p>
                                <p className="text-[11px] leading-tight text-gray-500 dark:text-gray-400">{s.rotulo}</p>
                            </div>
                        ))}
                    </div>
                </>
            )}

            <ConfirmationModal
                isOpen={confirmarLigar}
                onClose={() => setConfirmarLigar(false)}
                onConfirm={() => mudarModo('ligado')}
                title="Ligar o envio do acesso por WhatsApp?"
                description={`Os clientes da fila começam a receber o aviso do portal: até ${LIMITE_DIARIO} por dia, um a cada 5 minutos, de segunda a sexta, das 9h às 18h. Mande um teste antes, se ainda não mandou.`}
                confirmText="Ligar"
                variant="warning"
                isLoading={mudandoModo}
            />
        </div>
    );
};

export default EnvioAcessoPortalCard;
